import AdminTableFiltersCard from "../ui/AdminTableFiltersCard";

type AdminPaymentsFiltersProps = {
  search: string;
  paymentType: "GIG" | "BUSINESS_LISTING_FEE";
  status: string;
  createdFromDate: string;
  createdToDate: string;
  pageSize: number;
  onSearchChange: (value: string) => void;
  onPaymentTypeChange: (value: "GIG" | "BUSINESS_LISTING_FEE") => void;
  onStatusChange: (value: string) => void;
  onCreatedFromDateChange: (value: string) => void;
  onCreatedToDateChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
};

const AdminPaymentsFilters = ({
  search,
  paymentType,
  status,
  createdFromDate,
  createdToDate,
  pageSize,
  onSearchChange,
  onPaymentTypeChange,
  onStatusChange,
  onCreatedFromDateChange,
  onCreatedToDateChange,
  onPageSizeChange,
}: AdminPaymentsFiltersProps) => {
  return (
    <AdminTableFiltersCard>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[320px] flex-[2_1_360px]">
          <label htmlFor="admin-payments-filter-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Search
          </label>
          <input
            id="admin-payments-filter-search"
            type="text"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Gig title, teen, adult, guardian"
            className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-base text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>
        <div className="min-w-[180px] flex-[1_1_200px]">
          <label htmlFor="admin-payments-filter-type" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Type
          </label>
          <select
            id="admin-payments-filter-type"
            value={paymentType}
            onChange={(event) => onPaymentTypeChange(event.target.value as "GIG" | "BUSINESS_LISTING_FEE")}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="GIG">Gig payments</option>
            <option value="BUSINESS_LISTING_FEE">Business listing fees</option>
          </select>
        </div>
        <div className="min-w-[170px] flex-[1_1_180px]">
          <label htmlFor="admin-payments-filter-status" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Status
          </label>
          <select
            id="admin-payments-filter-status"
            value={status}
            onChange={(event) => onStatusChange(event.target.value)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="">All statuses</option>
            {paymentType === "GIG" ? <option value="CHECKOUT_PENDING">CHECKOUT_PENDING</option> : null}
            {paymentType === "GIG" ? <option value="FUNDED">FUNDED</option> : null}
            {paymentType === "GIG" ? <option value="RELEASE_READY">RELEASE_READY</option> : null}
            {paymentType === "GIG" ? <option value="PAID_OUT">PAID_OUT</option> : null}
            {paymentType === "GIG" ? <option value="REFUNDED">REFUNDED</option> : null}
            {paymentType === "GIG" ? <option value="TRANSFER_REVERSED">TRANSFER_REVERSED</option> : null}
            {paymentType === "BUSINESS_LISTING_FEE" ? <option value="CHECKOUT_PENDING">CHECKOUT_PENDING</option> : null}
            {paymentType === "BUSINESS_LISTING_FEE" ? <option value="PAID">PAID</option> : null}
            {paymentType === "BUSINESS_LISTING_FEE" ? <option value="FAILED">FAILED</option> : null}
            {paymentType === "BUSINESS_LISTING_FEE" ? <option value="EXPIRED">EXPIRED</option> : null}
            {paymentType === "GIG" ? <option value="FAILED">FAILED</option> : null}
          </select>
        </div>
        <div className="min-w-[150px] flex-[0_1_170px]">
          <label htmlFor="admin-payments-filter-created-from" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Created From
          </label>
          <input
            id="admin-payments-filter-created-from"
            type="date"
            value={createdFromDate}
            onChange={(event) => onCreatedFromDateChange(event.target.value)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>
        <div className="min-w-[150px] flex-[0_1_170px]">
          <label htmlFor="admin-payments-filter-created-to" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Created To
          </label>
          <input
            id="admin-payments-filter-created-to"
            type="date"
            min={createdFromDate || undefined}
            value={createdToDate}
            onChange={(event) => onCreatedToDateChange(event.target.value)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>
        <div className="min-w-[110px] flex-[0_1_120px]">
          <label htmlFor="admin-payments-filter-page-size" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Rows
          </label>
          <select
            id="admin-payments-filter-page-size"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </div>
      </div>
    </AdminTableFiltersCard>
  );
};

export default AdminPaymentsFilters;
